import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { format } from 'date-fns';
import { useData } from '../contexts/DataContext';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import TankSelector from '../components/TankSelector';
import TimestampDisplay from '../components/TimestampDisplay';
import SectionHeader from '../components/SectionHeader';
import LastUpdated from '../components/LastUpdated';

const { FiPlus, FiDroplet, FiTrash2, FiEdit3, FiShare2, FiAlertCircle } = FiIcons;

const WaterTestLog = () => {
  const { data, addWaterTest, deleteEntry, getActiveTank } = useData();
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    temperature: '',
    ph: '',
    ammonia: '',
    nitrite: '',
    nitrate: '',
    dissolvedOxygen: '',
    notes: ''
  });

  const activeTank = getActiveTank();

  const handleSubmit = (e) => {
    e.preventDefault();
    addWaterTest(formData);
    setFormData({
      temperature: '',
      ph: '',
      ammonia: '',
      nitrite: '',
      nitrate: '',
      dissolvedOxygen: '',
      notes: ''
    });
    setShowForm(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const getParameterStatus = (param, value) => {
    const ranges = {
      temperature: { min: 26, max: 30, unit: '°C' },
      ph: { min: 6.5, max: 8.5, unit: '' },
      ammonia: { min: 0, max: 0.5, unit: 'ppm' },
      nitrite: { min: 0, max: 0.5, unit: 'ppm' },
      nitrate: { min: 0, max: 40, unit: 'ppm' },
      dissolvedOxygen: { min: 5, max: 10, unit: 'mg/L' }
    };

    const range = ranges[param];
    if (!range || !value) return 'neutral';

    const numValue = parseFloat(value);
    if (numValue >= range.min && numValue <= range.max) return 'good';
    return 'warning';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'good':
        return 'text-green-600 bg-green-100';
      case 'warning':
        return 'text-red-600 bg-red-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const getIdealRangeText = (param) => {
    const ranges = {
      temperature: '26-30°C - Tilapia grow best in this range',
      ph: '6.5-8.5 - Stable pH is critical for fish health',
      ammonia: '<0.5 ppm - Highly toxic, should be minimal',
      nitrite: '<0.5 ppm - Toxic to fish, should be minimal',
      nitrate: '<40 ppm - Less toxic but high levels indicate poor water quality',
      dissolvedOxygen: '>5 mg/L - Essential for fish respiration'
    };
    return ranges[param] || '';
  };

  // Filter water tests for the active tank
  const tankWaterTests = data.waterTests.filter(test => 
    test.tankId === activeTank.id || !test.tankId // Include tests without tankId for backward compatibility
  );

  return (
    <div className="p-4 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Water Test Log</h1>
          <p className="text-gray-600 mt-1">Monitor water quality parameters</p>
          <TimestampDisplay />
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="flex items-center space-x-2 bg-aqua-500 text-white px-4 py-2 rounded-lg hover:bg-aqua-600 transition-colors"
        >
          <SafeIcon icon={FiPlus} className="w-4 h-4" />
          <span>Add Test</span>
        </button>
      </div>

      {/* Educational Tip */}
      <div className="bg-blue-50 border-l-4 border-blue-500 p-3 rounded">
        <div className="flex">
          <div className="flex-shrink-0">
            <SafeIcon icon={FiAlertCircle} className="h-5 w-5 text-blue-500" />
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-blue-800">Water Quality Tip</h3>
            <div className="mt-1 text-sm text-blue-700">
              Ammonia levels above 0.5 ppm can stress and kill fish. If ammonia rises, increase aeration, 
              reduce feeding temporarily, and perform partial water changes. Consider adding beneficial bacteria 
              products to help establish nitrification.
            </div>
          </div>
        </div>
      </div>

      {/* Tank Selector */}
      <TankSelector />

      {/* Add Test Form */}
      {showForm && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl p-6 shadow-sm border border-gray-200"
        >
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            New Water Test for {activeTank.name} ({activeTank.size >= 1000 ? `${activeTank.size/1000}kL` : `${activeTank.size}L`})
          </h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Temperature (°C)
                </label>
                <input
                  type="number"
                  name="temperature"
                  value={formData.temperature}
                  onChange={handleInputChange}
                  step="0.1"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-aqua-500 focus:border-aqua-500"
                  required
                />
                <p className="text-xs text-green-600 mt-1">Ideal: 26-30°C</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  pH
                </label>
                <input
                  type="number"
                  name="ph"
                  value={formData.ph}
                  onChange={handleInputChange}
                  step="0.1"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-aqua-500 focus:border-aqua-500"
                  required
                />
                <p className="text-xs text-green-600 mt-1">Ideal: 6.5-8.5</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Ammonia (ppm)
                </label>
                <input
                  type="number"
                  name="ammonia"
                  value={formData.ammonia}
                  onChange={handleInputChange}
                  step="0.1"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-aqua-500 focus:border-aqua-500"
                  required
                />
                <p className="text-xs text-green-600 mt-1">Ideal: &lt;0.5 ppm</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nitrite (ppm)
                </label>
                <input
                  type="number"
                  name="nitrite"
                  value={formData.nitrite}
                  onChange={handleInputChange}
                  step="0.1"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-aqua-500 focus:border-aqua-500"
                  required
                />
                <p className="text-xs text-green-600 mt-1">Ideal: &lt;0.5 ppm</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nitrate (ppm)
                </label>
                <input
                  type="number"
                  name="nitrate"
                  value={formData.nitrate}
                  onChange={handleInputChange}
                  step="0.1"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-aqua-500 focus:border-aqua-500"
                  required
                />
                <p className="text-xs text-green-600 mt-1">Ideal: &lt;40 ppm</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Dissolved Oxygen (mg/L)
                </label>
                <input
                  type="number"
                  name="dissolvedOxygen"
                  value={formData.dissolvedOxygen}
                  onChange={handleInputChange}
                  step="0.1"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-aqua-500 focus:border-aqua-500"
                  required
                />
                <p className="text-xs text-green-600 mt-1">Ideal: &gt;5 mg/L</p>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Notes
              </label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-aqua-500 focus:border-aqua-500"
                placeholder="Any observations or notes..."
              />
            </div>

            <div className="flex space-x-3">
              <button
                type="submit"
                className="flex-1 bg-aqua-500 text-white py-2 px-4 rounded-lg hover:bg-aqua-600 transition-colors"
              >
                Save Test
              </button>
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="flex-1 bg-gray-500 text-white py-2 px-4 rounded-lg hover:bg-gray-600 transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        </motion.div>
      )}

      {/* Water quality parameters guide */}
      <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
        <SectionHeader 
          title="Water Quality Guide" 
          infoText="Maintaining optimal water parameters is essential for tilapia health and growth. Regular testing helps identify issues before they affect your fish."
        />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-2">
          {[
            { key: 'temperature', label: 'Temperature', unit: '°C' },
            { key: 'ph', label: 'pH', unit: '' },
            { key: 'ammonia', label: 'Ammonia', unit: 'ppm' },
            { key: 'nitrite', label: 'Nitrite', unit: 'ppm' },
            { key: 'nitrate', label: 'Nitrate', unit: 'ppm' },
            { key: 'dissolvedOxygen', label: 'Dissolved O₂', unit: 'mg/L' }
          ].map((param) => (
            <div key={param.key} className="bg-gray-50 p-3 rounded-lg">
              <h3 className="font-medium text-gray-900">{param.label}</h3>
              <p className="text-sm text-gray-600 mt-1">{getIdealRangeText(param.key)}</p>
            </div>
          ))}
        </div>
        <div className="mt-4 p-3 bg-blue-50 rounded-lg">
          <h3 className="text-sm font-medium text-blue-800">Understanding Nitrogen Cycle</h3>
          <p className="text-sm text-blue-700 mt-1">
            In aquaculture, fish waste produces ammonia, which is converted to nitrite by Nitrosomonas bacteria. Nitrite is then converted to less toxic nitrate by Nitrobacter bacteria. This process is essential for maintaining water quality and preventing fish mortality.
          </p>
        </div>
      </div>

      {/* Water Test History */}
      <div className="space-y-4">
        <SectionHeader 
          title="Test History" 
          subtitle={`${tankWaterTests.length} records for ${activeTank.name}`}
        />
        
        {tankWaterTests.length > 0 ? (
          [...tankWaterTests].reverse().map((test) => (
            <motion.div
              key={test.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-xl p-4 shadow-sm border border-gray-200"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-aqua-100 rounded-lg flex items-center justify-center">
                    <SafeIcon icon={FiDroplet} className="w-5 h-5 text-aqua-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">
                      {format(new Date(test.date), 'MMM dd, yyyy')}
                    </h3>
                    <p className="text-sm text-gray-500">Water Quality Test</p>
                  </div>
                </div>
                <div className="flex space-x-2">
                  <button 
                    onClick={() => alert("This would share the water test data in a real application")}
                    className="p-2 text-aqua-600 hover:bg-aqua-50 rounded-lg transition-colors"
                  >
                    <SafeIcon icon={FiShare2} className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => deleteEntry('waterTests', test.id)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-4">
                {[
                  { key: 'temperature', label: 'Temperature', unit: '°C' },
                  { key: 'ph', label: 'pH', unit: '' },
                  { key: 'ammonia', label: 'Ammonia', unit: 'ppm' },
                  { key: 'nitrite', label: 'Nitrite', unit: 'ppm' },
                  { key: 'nitrate', label: 'Nitrate', unit: 'ppm' },
                  { key: 'dissolvedOxygen', label: 'Dissolved O₂', unit: 'mg/L' }
                ].map((param) => {
                  const status = getParameterStatus(param.key, test[param.key]);
                  const colorClass = getStatusColor(status);
                  return (
                    <div key={param.key} className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">{param.label}</span>
                      <div className="flex flex-col items-end">
                        <span className={`text-sm font-medium px-2 py-1 rounded ${colorClass}`}>
                          {test[param.key]} {param.unit}
                        </span>
                        {status === 'warning' && (
                          <span className="text-xs text-red-600">Outside ideal range</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {test.notes && (
                <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-700">{test.notes}</p>
                </div>
              )}
              
              <LastUpdated 
                timestamp={test.timestamp} 
                showSendUpdate={true}
                onSendUpdate={() => alert("This would send an update to team members in a real application")}
              />
            </motion.div>
          ))
        ) : (
          <div className="text-center py-12">
            <SafeIcon icon={FiDroplet} className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No water tests yet</h3>
            <p className="text-gray-500 mb-6">Start monitoring your water quality by adding your first test</p>
            <button
              onClick={() => setShowForm(true)}
              className="inline-flex items-center space-x-2 bg-aqua-500 text-white px-6 py-3 rounded-lg hover:bg-aqua-600 transition-colors"
            >
              <SafeIcon icon={FiPlus} className="w-5 h-5" />
              <span>Add First Test</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default WaterTestLog;